import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { BrushStart } from "../../../models/chart_model";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import DownloadButton from "../../../components/DownloadButton";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import {
  getChhattisgarhCumulativeBanking,
  getChhattisgarhDiscomData,
  getChhattisgarhForecastData,
  getChhattisgarhRealTimeForecast,
  getChhattisgarhRealTimeLoad15Mins,
  getTrendMetrics,
} from "../Utility/RestApi";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { formatDateForAPI } from "../const";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
// ─── Peak & Summary sub-views ────────────────────────────────────────────────
import { ChhattisgarhPeakAnalysis } from "./Forecast_LoadPeak";
import { ChhattisgarhSummaryAnalysis } from "./Forecast_LoadSummary";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

// ─── Intervals ───────────────────────────────────────────────────────────────
const Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const DISCOM_OPTIONS = ["CSPDCL"];
const UNITS = ["MW", "MWh", "LU"];

// Live polling — refresh actual load every 15 minutes while viewing today.
const POLL_INTERVAL_MS = 15 * 60 * 1000;

function isToday(d: Date | string | undefined): boolean {
  if (!d) return false;
  const dt = typeof d === "string" ? new Date(d) : d;
  const today = new Date();
  return (
    dt.getFullYear() === today.getFullYear() &&
    dt.getMonth() === today.getMonth() &&
    dt.getDate() === today.getDate()
  );
}

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

// ─── Chart suffixes — visible in Type filter ─────────────────────────────────
const CHART_SUFFIXES = [
  { suffix: "demand", displayName: "Actual Load", stackSuffix: "demand" },
  {
    suffix: "demand_forecast_d1",
    displayName: "Forecast D+1",
    stackSuffix: "forecast_d1",
  },
  {
    suffix: "demand_forecast_d2",
    displayName: "Forecast D+2",
    stackSuffix: "forecast_d2",
  },
  {
    suffix: "demand_forecast_rt",
    displayName: "Forecast RT",
    stackSuffix: "forecast_rt",
  },
  {
    suffix: "deviation",
    displayName: "Deviation %",
    stackSuffix: "deviation",
  },
  {
    suffix: "banking_drawal",
    displayName: "Banking Drawal",
    stackSuffix: "banking_drawal",
  },
  {
    suffix: "banking_inject",
    displayName: "Banking Return",
    stackSuffix: "banking_inject",
  },
];

// Banking series expand into an actual (solid) + scheduled (dashed) pair.
const BANKING_SUFFIXES: Record<string, { actual: string; sched: string }> = {
  banking_drawal: {
    actual: "banking_drawal_actual",
    sched: "banking_drawal_sched",
  },
  banking_inject: {
    actual: "banking_inject_actual",
    sched: "banking_inject_sched",
  },
};

const BANKING_DISPLAY: Record<string, string> = {
  banking_drawal_actual: "Banking Drawal",
  banking_drawal_sched: "Banking Drawal (Scheduled)",
  banking_inject_actual: "Banking Return",
  banking_inject_sched: "Banking Return (Scheduled)",
};

const BANKING_COLORS: Record<string, string> = {
  banking_drawal_actual: "#2e7d32",
  banking_drawal_sched: "#66bb6a",
  banking_inject_actual: "#c62828",
  banking_inject_sched: "#ef9a9a",
};

// Internal suffixes that get enabled when "Deviation %" is selected
const DEVIATION_SUFFIXES = [
  {
    suffix: "deviation_d1",
    displayName: "(Forecast D+1) Deviation %",
    stackSuffix: "deviation_d1",
  },
  {
    suffix: "deviation_d2",
    displayName: "(Forecast D+2) Deviation %",
    stackSuffix: "deviation_d2",
  },
  {
    suffix: "deviation_rt",
    displayName: "(RealTime) Deviation %",
    stackSuffix: "deviation_rt",
  },
];

// ─── Helper ───────────────────────────────────────────────────────────────────
function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function ChhattisgarhForecast() {
  const dispatch = useDispatch();
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  // ── State ─────────────────────────────────────────────────────────────────
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [selectedSuffix, setSelectedSuffix] = useState(
    CHART_SUFFIXES.filter((s) =>
      [
        "demand",
        "demand_forecast_d1",
        "demand_forecast_d2",
        "demand_forecast_rt",
        "deviation",
        "banking_drawal",
        "banking_inject",
      ].includes(s.suffix),
    ),
  );

  // True when D+2 forecast has at least one non-zero value in the fetched data
  const hasD2Data = useMemo(
    () => data.some((item) => (item.demand_forecast_d2_mw || 0) !== 0),
    [data],
  );

  // Expand "deviation" into d1/d2 based on which forecasts are selected
  // Sort order: Actual Load → Forecasts → Deviations
  const SUFFIX_ORDER: Record<string, number> = {
    demand: 0,
    demand_forecast_d1: 1,
    demand_forecast_d2: 2,
    demand_forecast_rt: 3,
    deviation_d1: 4,
    deviation_d2: 5,
    deviation_rt: 6,
  };

  // True when RealTime forecast has at least one non-zero value
  const hasRtData = useMemo(
    () => data.some((item) => (item.demand_forecast_rt_mw || 0) !== 0),
    [data],
  );

  const expandedSuffix = useMemo(() => {
    const hasD1 = selectedSuffix.some((s) => s.suffix === "demand_forecast_d1");
    const hasD2 = selectedSuffix.some((s) => s.suffix === "demand_forecast_d2");
    const hasRt = selectedSuffix.some((s) => s.suffix === "demand_forecast_rt");
    const result: typeof CHART_SUFFIXES = [];
    selectedSuffix.forEach((s) => {
      if (s.suffix === "deviation") {
        if (hasD1) result.push(DEVIATION_SUFFIXES[0]);
        if (hasD2 && hasD2Data) result.push(DEVIATION_SUFFIXES[1]);
        if (hasRt && hasRtData) result.push(DEVIATION_SUFFIXES[2]);
      } else if (BANKING_SUFFIXES[s.suffix]) {
        // Expand a banking filter into its actual (solid) + scheduled (dashed) pair
        const pair = BANKING_SUFFIXES[s.suffix];
        [pair.actual, pair.sched].forEach((sub) =>
          result.push({
            suffix: sub,
            displayName: BANKING_DISPLAY[sub],
            stackSuffix: sub,
          }),
        );
      } else if (s.suffix === "demand_forecast_d2" && !hasD2Data) {
        // Skip D+2 forecast when no D+2 data exists
      } else if (s.suffix === "demand_forecast_rt" && !hasRtData) {
        // Skip RealTime forecast when no RT data exists
      } else {
        result.push(s);
      }
    });
    result.sort(
      (a, b) => (SUFFIX_ORDER[a.suffix] ?? 99) - (SUFFIX_ORDER[b.suffix] ?? 99),
    );
    return result;
  }, [selectedSuffix, hasD2Data, hasRtData]);

  // ── Chart object builder ──────────────────────────────────────────────────
  const DEVIATION_COLORS = [
    "#00c8b4",
    "#ff69b4",
    "#adff2f",
    "#e040fb",
    "#ffdd00",
    "#40c4ff",
  ];

  const D2_COLORS = ["#ab47bc", "#26c6da", "#d4e157", "#ffa726", "#ec407a"];

  const RT_COLORS = ["#1e88e5", "#1565c0", "#42a5f5", "#0d47a1", "#64b5f6"];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    _interval: { isTime: boolean },
    _chartType: { value: string; stackId?: string },
  ) => {
    const unit = selectedUnit.toLowerCase();

    // ── Banking series (drawal / inject, actual solid + scheduled dashed) ──
    // Four fully-independent line series — never stacked, always drawn as a
    // line regardless of the selected chart type, so each is visible on its own.
    const isBanking = suffix in BANKING_COLORS;
    if (isBanking) {
      const isSched = suffix.endsWith("_sched");
      return {
        dataKey: `${key}_${suffix}_${unit}`,
        name: `${normalizeDisplayLabelWithoutUndefined(key)} ${BANKING_DISPLAY[suffix]}`.trim(),
        legendColor: BANKING_COLORS[suffix],
        stroke: BANKING_COLORS[suffix],
        isDash: isSched,
        type: "line",
        stackId: undefined,
        showSymbol: false,
      };
    }

    const isDeviation =
      suffix === "deviation_d1" ||
      suffix === "deviation_d2" ||
      suffix === "deviation_rt";
    const deviationType =
      suffix === "deviation_d1"
        ? "d1"
        : suffix === "deviation_d2"
          ? "d2"
          : "rt";

    const baseColor = ExchangeColors[index % ExchangeColors.length];
    const color =
      suffix === "demand"
        ? baseColor
        : suffix === "demand_forecast_d1"
          ? lightenColor(baseColor, 30)
          : suffix === "demand_forecast_d2"
            ? D2_COLORS[index % D2_COLORS.length]
            : suffix === "demand_forecast_rt"
              ? RT_COLORS[index % RT_COLORS.length]
              : suffix === "deviation_d1"
                ? DEVIATION_COLORS[index % DEVIATION_COLORS.length]
                : suffix === "deviation_d2"
                  ? DEVIATION_COLORS[(index % DEVIATION_COLORS.length) + 1]
                  : suffix === "deviation_rt"
                    ? DEVIATION_COLORS[(index % DEVIATION_COLORS.length) + 2]
                    : "#ff9f40";

    return {
      dataKey: `${key}_${suffix}_${unit}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`.trim(),
      yAxisId: isDeviation ? "right" : undefined,
      legendColor: color,
      stroke: color,
      // RealTime forecast renders as a dashed line to distinguish it from the
      // solid Actual / D+1 / D+2 series.
      ...(suffix === "demand_forecast_rt" && { isDash: true }),
      type: _chartType.value,
      stackId:
        !isDeviation && _chartType.stackId !== undefined
          ? stackSuffix
          : undefined,
      ...(isDeviation && {
        unit: "%",
        deviationValueKey: `${key}_deviation_${deviationType}_value_${unit}`,
      }),
    };
  };

  // ── Fetch data — single defaultRangeDate ──────────────────────────────────
  const _handlefetchData = useCallback(
    async (silent = false) => {
      if (!DefaultDates[activeTab]?.defaultRangeDate) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);

      try {
        const dates = DefaultDates[activeTab].defaultRangeDate;
        const startDate = formatDateForAPI(dates.startDate);
        const endDate = formatDateForAPI(dates.endDate);

        // For a single day equal to today, source the actual load from the
        // 15-min real-time endpoint; all other ranges keep using getDemand.
        const today = formatDateForAPI(new Date());
        const isCurrentDateOnly = startDate === endDate && startDate === today;

        const [actualItems, forecastItems, realTimeItems, bankingItems] =
          await Promise.all([
            isCurrentDateOnly
              ? getChhattisgarhRealTimeLoad15Mins(startDate, endDate)
              : getChhattisgarhDiscomData(startDate, endDate),
            getChhattisgarhForecastData(startDate, endDate),
            getChhattisgarhRealTimeForecast(startDate, endDate),
            getChhattisgarhCumulativeBanking(startDate, endDate),
          ]);

        // ── Same pattern as TrendAnalysis: keep actual and forecast as separate
        // rows, then append into one flat array. FormatChartData groups by
        // date+block and sums all rows sharing that key, so each field gets
        // its contribution independently — forecast shows even when actual is
        // empty (future dates) and vice versa.

        // Actual rows — forecast fields zero-filled.
        // month/dateIndex/dates are already set by the RestApi mapper via ...item
        const actualRows = actualItems.map((item: any) => ({
          ...item,
          demand_forecast_d1_mw: 0,
          demand_forecast_d1_mwh: 0,
          demand_forecast_d1_lu: 0,
          demand_forecast_d2_mw: 0,
          demand_forecast_d2_mwh: 0,
          demand_forecast_d2_lu: 0,
          demand_forecast_rt_mw: 0,
          demand_forecast_rt_mwh: 0,
          demand_forecast_rt_lu: 0,
        }));

        // Forecast rows — actual fields zero-filled; D+1 and D+2 split by forecast_type.
        // RealTime (T+RT) is carried in a separate row stream below, so here a
        // non-"T+1" type is treated as D+2.
        // month/dateIndex/dates are already set by the RestApi mapper via ...item
        const forecastRows = forecastItems.map((item: any) => {
          const fType = item.forecast_type || "T+1";
          const isD1 = fType === "T+1";
          const val = item.demand_forecast_mw || 0;
          return {
            ...item,
            // Actual fields are zero — this row carries only forecast values
            demand_mw: 0,
            demand_mwh: 0,
            demand_lu: 0,
            demand_forecast_d1_mw: isD1 ? val : 0,
            demand_forecast_d1_mwh: isD1 ? val / 4 : 0,
            demand_forecast_d1_lu: isD1 ? val / 4 / 100 : 0,
            demand_forecast_d2_mw: isD1 ? 0 : val,
            demand_forecast_d2_mwh: isD1 ? 0 : val / 4,
            demand_forecast_d2_lu: isD1 ? 0 : val / 4 / 100,
            demand_forecast_rt_mw: 0,
            demand_forecast_rt_mwh: 0,
            demand_forecast_rt_lu: 0,
          };
        });

        // RealTime forecast rows — only the RT forecast fields carry values.
        const realTimeRows = realTimeItems.map((item: any) => {
          const val = item.demand_forecast_mw || 0;
          return {
            ...item,
            demand_mw: 0,
            demand_mwh: 0,
            demand_lu: 0,
            demand_forecast_d1_mw: 0,
            demand_forecast_d1_mwh: 0,
            demand_forecast_d1_lu: 0,
            demand_forecast_d2_mw: 0,
            demand_forecast_d2_mwh: 0,
            demand_forecast_d2_lu: 0,
            demand_forecast_rt_mw: val,
            demand_forecast_rt_mwh: val / 4,
            demand_forecast_rt_lu: val / 4 / 100,
          };
        });

        // Banking rows — carry only the banking fields (mapper already split
        // actual vs scheduled by period). Demand/forecast fields stay zero so the
        // FormatChartData sum keeps each series independent.
        const bankingRows = bankingItems.map((item: any) => ({
          ...item,
          demand_mw: 0,
          demand_mwh: 0,
          demand_lu: 0,
          demand_forecast_d1_mw: 0,
          demand_forecast_d1_mwh: 0,
          demand_forecast_d1_lu: 0,
          demand_forecast_d2_mw: 0,
          demand_forecast_d2_mwh: 0,
          demand_forecast_d2_lu: 0,
          demand_forecast_rt_mw: 0,
          demand_forecast_rt_mwh: 0,
          demand_forecast_rt_lu: 0,
        }));

        // Deduplicate: one actual row and one D+1/D+2/RT row per date+block is enough
        const seen = new Set<string>();
        const deduped = [
          ...actualRows,
          ...forecastRows,
          ...realTimeRows,
          ...bankingRows,
        ].filter((item) => {
          const k = `${item.date}_${item.block}_${item.demand_forecast_d1_mw}_${item.demand_forecast_d2_mw}_${item.demand_forecast_rt_mw}_${item.banking_drawal_actual_mw ?? ""}_${item.banking_drawal_sched_mw ?? ""}_${item.banking_inject_actual_mw ?? ""}_${item.banking_inject_sched_mw ?? ""}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });

        setData(deduped);
      } catch (err) {
        console.error("Error in data fetching:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [DefaultDates, activeTab],
  );

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // Live polling: when the selected range is the current date, silently
  // re-fetch the actual load every 15 minutes so the chart stays current.
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const dates = DefaultDates?.[activeTab]?.defaultRangeDate;
    if (!dates) return;
    if (isToday(dates.startDate) && isToday(dates.endDate)) {
      pollRef.current = setInterval(() => {
        _handlefetchData(true);
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [DefaultDates, activeTab, _handlefetchData]);

  // ── Fetch metrics ─────────────────────────────────────────────────────────
  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const ranges = [
        {
          startDate: formatDateForAPI(dates.startDate),
          endDate: formatDateForAPI(dates.endDate),
          blocks: selectedBlock.map((block: any) => block.value),
        },
      ];
      // Send only forecast types — same pattern as TrendAnalysis
      const selectedTypes = selectedSuffix
        .map((s) => s.displayName)
        .filter((s) => s === "Forecast D+1");
      const metricsData = await getTrendMetrics({
        ranges,
        aggregated: true,
        selectedTypes,
      });
      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock, selectedSuffix]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ── Format chart data ─────────────────────────────────────────────────────
  useEffect(() => {
    let filteredData = [...data];

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((o) => o.value).includes(item.block),
      );
    }

    filteredData.sort((a, b) => {
      const d = new Date(a.date).getTime() - new Date(b.date).getTime();
      return d !== 0 ? d : a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") return ["date", "block"];
      if (selectedInterval.key === "date") return ["date"];
      return ["month"];
    }

    const aggregationKeys = [
      "demand_mw",
      "demand_mwh",
      "demand_lu",
      "demand_forecast_d1_mw",
      "demand_forecast_d1_mwh",
      "demand_forecast_d1_lu",
      "demand_forecast_d2_mw",
      "demand_forecast_d2_mwh",
      "demand_forecast_d2_lu",
      "demand_forecast_rt_mw",
      "demand_forecast_rt_mwh",
      "demand_forecast_rt_lu",
      "banking_drawal_actual_mw",
      "banking_drawal_actual_mwh",
      "banking_drawal_actual_lu",
      "banking_drawal_sched_mw",
      "banking_drawal_sched_mwh",
      "banking_drawal_sched_lu",
      "banking_inject_actual_mw",
      "banking_inject_actual_mwh",
      "banking_inject_actual_lu",
      "banking_inject_sched_mw",
      "banking_inject_sched_mwh",
      "banking_inject_sched_lu",
    ];

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: [],
      tableKeys: ["date", "block", "month"],
      volumeKey: "",
      aggregationKeys,
      averageKeys: aggregationKeys,
      averageDuplicationKeys: ["date", "block", "month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    const unit = selectedUnit.toLowerCase();

    // Compute deviation % for D+1 and D+2
    const processedData = cData.data.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };
      cData.keys.forEach((key) => {
        const actualVal = dataPoint[`${key}_demand_${unit}`] ?? 0;
        const d1Val = dataPoint[`${key}_demand_forecast_d1_${unit}`] ?? 0;
        const d2Val = dataPoint[`${key}_demand_forecast_d2_${unit}`] ?? 0;
        const rtVal = dataPoint[`${key}_demand_forecast_rt_${unit}`] ?? 0;

        // Null out zero actual load so the line floats (no drop to 0) on
        // future blocks where no actual data exists — same as Trend Analysis.
        if (actualVal === 0) newDataPoint[`${key}_demand_${unit}`] = null;

        // Null out zero forecast values so the chart line is not drawn
        if (d1Val === 0)
          newDataPoint[`${key}_demand_forecast_d1_${unit}`] = null;
        if (d2Val === 0)
          newDataPoint[`${key}_demand_forecast_d2_${unit}`] = null;
        if (rtVal === 0)
          newDataPoint[`${key}_demand_forecast_rt_${unit}`] = null;

        // Null out zero banking values so flat zero lines are not drawn.
        // (boundary back-fill happens in a follow-up pass below)
        [
          "banking_drawal_actual",
          "banking_drawal_sched",
          "banking_inject_actual",
          "banking_inject_sched",
        ].forEach((b) => {
          if ((dataPoint[`${key}_${b}_${unit}`] ?? 0) === 0)
            newDataPoint[`${key}_${b}_${unit}`] = null;
        });

        // D+1 deviation
        if (actualVal !== 0 && d1Val !== 0) {
          newDataPoint[`${key}_deviation_d1_${unit}`] =
            (Math.abs(actualVal - d1Val) / actualVal) * 100;
          newDataPoint[`${key}_deviation_d1_value_${unit}`] = Math.abs(
            actualVal - d1Val,
          );
        } else {
          newDataPoint[`${key}_deviation_d1_${unit}`] = null;
          newDataPoint[`${key}_deviation_d1_value_${unit}`] = null;
        }

        // D+2 deviation
        if (actualVal !== 0 && d2Val !== 0) {
          newDataPoint[`${key}_deviation_d2_${unit}`] =
            (Math.abs(actualVal - d2Val) / actualVal) * 100;
          newDataPoint[`${key}_deviation_d2_value_${unit}`] = Math.abs(
            actualVal - d2Val,
          );
        } else {
          newDataPoint[`${key}_deviation_d2_${unit}`] = null;
          newDataPoint[`${key}_deviation_d2_value_${unit}`] = null;
        }

        // RealTime deviation
        if (actualVal !== 0 && rtVal !== 0) {
          newDataPoint[`${key}_deviation_rt_${unit}`] =
            (Math.abs(actualVal - rtVal) / actualVal) * 100;
          newDataPoint[`${key}_deviation_rt_value_${unit}`] = Math.abs(
            actualVal - rtVal,
          );
        } else {
          newDataPoint[`${key}_deviation_rt_${unit}`] = null;
          newDataPoint[`${key}_deviation_rt_value_${unit}`] = null;
        }
      });
      return newDataPoint;
    });

    // Table-only: merge each banking pair into a single value per block so the
    // Table / Summary views show one "Banking Drawal" / "Banking Return" column.
    // Mirror the chart line: show the actual value while it exists, then continue
    // with the scheduled value once actual runs out (and use the scheduled value
    // for any block that has no actual). Computed BEFORE the boundary back-fill
    // below so the chart's connecting-point trick never leaks into the table.
    processedData.forEach((dp) => {
      cData.keys.forEach((key) => {
        (["banking_drawal", "banking_inject"] as const).forEach((b) => {
          const actual = dp[`${key}_${b}_actual_${unit}`];
          const sched = dp[`${key}_${b}_sched_${unit}`];
          dp[`${key}_${b}_${unit}`] = actual != null ? actual : (sched ?? null);
        });
      });
    });

    // Connect the solid (actual) and dashed (scheduled) banking lines: copy the
    // last actual value onto the first scheduled point of each series so there
    // is no visual gap at the past→future boundary. (Chart-only; runs after the
    // table merge above so it does not affect the merged column.)
    cData.keys.forEach((key) => {
      (["banking_drawal", "banking_inject"] as const).forEach((b) => {
        const actualKey = `${key}_${b}_actual_${unit}`;
        const schedKey = `${key}_${b}_sched_${unit}`;
        for (let i = 1; i < processedData.length; i++) {
          const prev = processedData[i - 1];
          const cur = processedData[i];
          if (
            cur[schedKey] != null &&
            prev[schedKey] == null &&
            prev[actualKey] != null
          ) {
            cur[schedKey] = prev[actualKey];
            break;
          }
        }
      });
    });

    setChartData(processedData);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedInterval, selectedBlock, selectedSuffix, selectedUnit]);

  // ── Legends ───────────────────────────────────────────────────────────────
  const legends = useMemo(
    () =>
      expandedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
          ),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      expandedSuffix,
      chartKeys,
      selectedInterval,
      selectedChartType,
      selectedUnit,
    ],
  );

  // Preserve legend order from expandedSuffix (Actual → Forecast → Deviation)
  const customLegendOrder = useMemo(
    () => legends.map((l) => l.name),
    [legends],
  );

  // ── Tooltip: append deviation value (quantum) to Deviation % series name ──
  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      if (!seriesName.includes("Deviation %")) return seriesName;
      const legend = (legends as any[]).find((l) => l.name === seriesName);
      const devVal = legend?.deviationValueKey
        ? dataPoint?.[legend.deviationValueKey]
        : undefined;
      if (devVal === null || devVal === undefined) return seriesName;
      const devFormatted = Math.abs(parseFloat(devVal))
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${seriesName} (${devFormatted} ${selectedUnit})`;
    },
    [legends, selectedUnit],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleXAxisModeChange(_item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(unit: string) {
    setSelectedUnit(unit);
  }

  function handleTypeChange(selected: string[]) {
    setSelectedSuffix(
      CHART_SUFFIXES.filter((s) => selected.includes(s.displayName)),
    );
    dispatch(setLoadingFilterName(""));
  }

  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  const xDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "block";
    return selectedInterval.values[0];
  }, [selectedInterval]);

  const secondXDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "date";
    return undefined;
  }, [selectedInterval.isTime]);

  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends as any);

  // Banking renders as 2 logical lines (Drawal, Inject), each solid for the
  // actual/past portion and dashed for the scheduled/future portion. The
  // scheduled series still draw, but their legend chips are hidden so the
  // legend shows only "Banking Drawal" and "Banking Return".
  const hiddenLegendNames = useMemo(
    () =>
      (legends as any[])
        .filter((l) => /\(Scheduled\)$/.test(l?.name ?? ""))
        .map((l) => l.name as string),
    [legends],
  );

  // Table / Summary legends: collapse each banking pair (actual solid +
  // scheduled dashed) into a SINGLE legend whose dataKey points to the merged
  // value computed in chartData. This makes the Table / Summary views show one
  // "Banking Drawal" / "Banking Return" column instead of two. The chart keeps
  // using `legends` (the full split pair), so it is unchanged.
  const unitLower = selectedUnit.toLowerCase();
  const tableLegends = useMemo(() => {
    const out: any[] = [];
    const seenMerged = new Set<string>();
    (visibleLegends as any[]).forEach((leg) => {
      const m = String(leg.dataKey ?? "").match(
        /^(.*)_(banking_drawal|banking_inject)_(?:actual|sched)_[a-z]+$/,
      );
      if (m) {
        const [, key, b] = m;
        const mergedKey = `${key}_${b}_${unitLower}`;
        if (seenMerged.has(mergedKey)) return; // one column per pair
        seenMerged.add(mergedKey);
        const baseName = String(leg.name ?? "").replace(
          /\s*\(Scheduled\)\s*$/,
          "",
        );
        out.push({
          ...leg,
          dataKey: mergedKey,
          name: baseName,
          isDash: undefined,
        });
        return;
      }
      out.push(leg);
    });
    return out;
  }, [visibleLegends, unitLower]);

  const commonSummaryProps = {
    legends: tableLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "CSPDCL Forecast Load",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ── Props forwarded to Peak / Summary sub-views ───────────────────────────
  const sharedSubViewProps = {
    onXAxisModeChange: handleXAxisModeChange,
    selectedUnit,
    onUnitChange: handleUnitChange,
    selectedDiscom,
    onDiscomChange: handleDiscomChange,
    selectedGroupKeys: "Discom Load",
    onGroupByChange: () => {},
    selectedSuffix,
    onTypeChange: handleTypeChange,
    chartSuffixes: CHART_SUFFIXES,
    metrics,
  };

  // ── Peak / Summary routes ─────────────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return <ChhattisgarhPeakAnalysis {...sharedSubViewProps} />;
  }

  if (selectedXAxisMode.value === "summary") {
    return <ChhattisgarhSummaryAnalysis {...sharedSubViewProps} />;
  }

  // ── Main Intervals render ─────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((i) => i.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={Intervals.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_item, index) => setSelectedInterval(Intervals[index])}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => handleUnitChange(item)}
          />

          {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={handleDiscomChange}
          /> */}

          <FilterMenu
            title="Type"
            items={CHART_SUFFIXES.map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={handleTypeChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Forecast Discom Load"
              subtitle="Fetching Chhattisgarh forecast discom load data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading flex items-center gap-x-4">
                  Forecast Discom Load ({selectedUnit})
                  {selectedSuffix.some(
                    (s) =>
                      s.suffix === "demand" ||
                      s.suffix === "demand_forecast_rt",
                  ) &&
                    isToday(
                      DefaultDates[activeTab]?.defaultRangeDate?.startDate,
                    ) &&
                    isToday(
                      DefaultDates[activeTab]?.defaultRangeDate?.endDate,
                    ) && (
                      <span
                        title="Live — auto-refreshes every 15 minutes"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span className="gna-live-sonar">
                          <span className="gna-live-sonar__dot" />
                          <span className="gna-live-sonar__ring" />
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#22c55e",
                            fontWeight: 600,
                            letterSpacing: 0.5,
                          }}
                        >
                          LIVE
                        </span>
                      </span>
                    )}
                </h2>

                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((i) => i.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <DownloadButton
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(commonSummaryProps);
                      if (dataTab.label === "Summary")
                        downloadSummaryCSV(commonSummaryProps);
                      if (dataTab.label === "Table")
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                  />
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  brushIndex={
                    chartData.length > 96
                      ? { startIndex: 0, endIndex: 95 }
                      : undefined
                  }
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  secondYAxisDomain={[0, 100]}
                  secondYAxisLabel={
                    selectedSuffix.some((s) => s.suffix === "deviation")
                      ? "Deviation %"
                      : undefined
                  }
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  hiddenLegends={hiddenLegendNames}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                  key={`${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}`}
                  xDataKey={xDataKey}
                  secondXDataKey={secondXDataKey}
                  xTick={
                    selectedInterval.isTime
                      ? (value: any) => renderHourTick(value, 10)
                      : undefined
                  }
                  customLegendOrder={customLegendOrder}
                  resolveDisplayName={resolveTooltipName}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}

              {dataTab.label === "Table" && (
                <ChartTable
                  {...commonSummaryProps}
                  defaultSortDirection="asc"
                />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
    1. Actual Load data is sourced from CSPDCL's official records.
    2. Forecast Load data is provided by GNA Energy."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
